import React, { Component } from 'react';
import { observer } from 'mobx-react';
import { Table, Checkbox, Input, Button, TextArea, Modal } from 'semantic-ui-react';
import { parseLoomId } from '../../pagesv2/TrainingPlan/trainingPlan';

// Mirrors CONSENT_MESSAGE_MAX_LENGTH in backend/libs/consentNotice.js —
// Google Meet truncates chat messages at 500 characters.
const CONSENT_MESSAGE_MAX_LENGTH = 500;

// Per-company Training Plan Looms editable here (Multiply admins only — the
// field is in patchHandler's ADMIN_ONLY_FIELDS). Accepts a share/embed URL or
// a bare id; only the id is stored, so the customer-facing iframe can never be
// pointed at an arbitrary URL. Swapping a video is a data edit, not a deploy.
// Sticky within the grid's own scroll container. Solid background so rows
// don't show through while scrolled; matches Semantic's header color.
const HEADER_STICKY = {
    position: 'sticky',
    top: 0,
    zIndex: 3,
    background: '#f9fafb',
    boxShadow: 'inset 0 -1px 0 rgba(34, 36, 38, 0.1)',
};

const VIDEO_SLOTS = [
    { key: 'intro', placeholder: 'Intro Loom (URL or id)' },
    { key: 'elevatorPitch', placeholder: 'Elevator Pitch example' },
    { key: 'casualEncounter', placeholder: 'Casual Encounter example' },
];

class CompaniesGrid extends Component {
    state = {
        loomDrafts: {}, loomSavingId: null, loomErrorId: null,
        consentDrafts: {}, consentSavingId: null, consentError: null, // { companyId, message }
        consentModalCompanyId: null, // company whose wording editor modal is open
        bccDrafts: {}, bccSavingId: null, bccError: null, // { companyId, message }
        bccModalCompanyId: null,
        tableScrollWidth: 0, // inner width of the table, drives the floating scrollbar
        stickyVisible: false, stickyLeft: 0, stickyWidth: 0, // fixed-bar geometry
    };

    // ---- Floating horizontal scrollbar ------------------------------------
    // The grid is taller than the viewport, so the container's native
    // horizontal scrollbar sits below the fold. A position:FIXED strip at the
    // bottom of the window mirrors it, two-way synced. Fixed (not sticky) on
    // purpose: the v2 shell scrolls content in a nested container, and sticky
    // silently pins to the wrong scrollport there. Geometry is recomputed
    // from the grid's bounding rect on every scroll (capture phase catches
    // nested scrollers) and resize; the strip hides itself whenever the
    // grid's own scrollbar is on screen or the grid is out of view.
    tableScrollRef = React.createRef();
    stickyScrollRef = React.createRef();
    syncingScroll = false;

    componentDidMount() {
        this.updateStickyGeometry();
        window.addEventListener('scroll', this.updateStickyGeometry, true);
        window.addEventListener('resize', this.updateStickyGeometry);
        if (typeof ResizeObserver !== 'undefined' && this.tableScrollRef.current) {
            this.resizeObserver = new ResizeObserver(this.updateStickyGeometry);
            this.resizeObserver.observe(this.tableScrollRef.current);
            const table = this.tableScrollRef.current.firstElementChild;
            if (table) this.resizeObserver.observe(table);
        }
    }

    componentDidUpdate() {
        // Row/column changes (new companies loading in) change the geometry.
        this.updateStickyGeometry();
    }

    componentWillUnmount() {
        window.removeEventListener('scroll', this.updateStickyGeometry, true);
        window.removeEventListener('resize', this.updateStickyGeometry);
        if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    updateStickyGeometry = () => {
        const el = this.tableScrollRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const viewportH = window.innerHeight;
        const next = {
            // Show only while the grid crosses the bottom edge of the window:
            // once the grid's own scrollbar scrolls into view, hand back to it.
            stickyVisible:
                el.scrollWidth > el.clientWidth + 1 &&
                rect.top < viewportH - 40 &&
                rect.bottom > viewportH,
            stickyLeft: rect.left,
            stickyWidth: el.clientWidth,
            tableScrollWidth: el.scrollWidth,
        };
        const s = this.state;
        if (next.stickyVisible !== s.stickyVisible || next.stickyLeft !== s.stickyLeft ||
            next.stickyWidth !== s.stickyWidth || next.tableScrollWidth !== s.tableScrollWidth) {
            this.setState(next, () => {
                // Keep the mirror aligned when it (re)appears.
                if (this.stickyScrollRef.current && this.tableScrollRef.current) {
                    this.stickyScrollRef.current.scrollLeft = this.tableScrollRef.current.scrollLeft;
                }
            });
        }
    };

    // Two-way sync with a re-entrancy flag: scrollLeft assignment fires the
    // other element's onScroll, which would bounce back forever without it.
    syncScroll = (from, to) => {
        if (this.syncingScroll) { this.syncingScroll = false; return; }
        if (!from.current || !to.current) return;
        this.syncingScroll = true;
        to.current.scrollLeft = from.current.scrollLeft;
    };

    // After toggling a feature flag, the edited Company is a separate MST
    // instance from companyStore.company (the "current company" the router
    // reads). If we just edited the current company, re-fetch it so gated
    // routes/UI reflect the change without a re-login or company switch.
    syncCurrentCompany = async (companyId) => {
        const { companyStore } = this.props;
        if (companyStore?.company?.id && companyStore.company.id === companyId) {
            await companyStore.loadCompany(companyId);
        }
    }

    draftKey = (companyId, slotKey) => `${companyId}:${slotKey}`;

    loomValue = (company, slotKey) => {
        const key = this.draftKey(company.id, slotKey);
        if (key in this.state.loomDrafts) return this.state.loomDrafts[key];
        return company.trainingPlanVideos?.[slotKey]?.loomId || '';
    };

    loomsDirty = (company) => VIDEO_SLOTS.some(({ key }) => {
        const draftKey = this.draftKey(company.id, key);
        return draftKey in this.state.loomDrafts &&
            this.state.loomDrafts[draftKey] !== (company.trainingPlanVideos?.[key]?.loomId || '');
    });

    setLoomDraft = (company, slotKey, value) => {
        this.setState((prev) => ({
            loomDrafts: { ...prev.loomDrafts, [this.draftKey(company.id, slotKey)]: value },
            loomErrorId: prev.loomErrorId === company.id ? null : prev.loomErrorId,
        }));
    };

    saveLooms = async (company) => {
        const videos = {};
        for (const { key } of VIDEO_SLOTS) {
            const raw = this.loomValue(company, key).trim();
            if (!raw) continue; // empty input = remove the slot
            const loomId = parseLoomId(raw);
            if (!loomId) {
                this.setState({ loomErrorId: company.id });
                return;
            }
            videos[key] = { ...(company.trainingPlanVideos?.[key] || {}), loomId };
        }
        this.setState({ loomSavingId: company.id, loomErrorId: null });
        try {
            await company.updateTrainingPlanVideos(videos);
            await this.syncCurrentCompany(company.id);
            this.setState((prev) => {
                const loomDrafts = { ...prev.loomDrafts };
                VIDEO_SLOTS.forEach(({ key }) => delete loomDrafts[this.draftKey(company.id, key)]);
                return { loomDrafts };
            });
        } catch (error) {
            // Surface it — console-only meant a failed network save was
            // indistinguishable from success for the admin.
            console.error('Failed to save Training Plan Looms:', error);
            this.setState({ loomErrorId: company.id });
        } finally {
            this.setState({ loomSavingId: null });
        }
    };

    renderLoomCell(company) {
        return (
            <div style={{ minWidth: '190px' }}>
                {VIDEO_SLOTS.map(({ key, placeholder }) => (
                    <Input
                        key={key}
                        size="mini"
                        fluid
                        placeholder={placeholder}
                        value={this.loomValue(company, key)}
                        onChange={(e) => this.setLoomDraft(company, key, e.target.value)}
                        style={{ marginBottom: '4px' }}
                    />
                ))}
                {this.state.loomErrorId === company.id && (
                    <div style={{ color: '#dc3545', fontSize: '11px', marginBottom: '4px' }}>
                        Couldn't read a Loom id from one of the values. Paste the share URL or the bare id.
                    </div>
                )}
                {this.loomsDirty(company) && (
                    <Button
                        size="mini"
                        primary
                        loading={this.state.loomSavingId === company.id}
                        onClick={() => this.saveLooms(company)}
                    >
                        Save Looms
                    </Button>
                )}
            </div>
        );
    }

    // ---- Consent notice (in-meeting recording disclosure) -----------------
    // Draft holds the WHOLE notice object per company; falls back to what's
    // stored. Empty message = Multiply default wording at bot-schedule time.
    consentValue = (company) => {
        if (company.id in this.state.consentDrafts) return this.state.consentDrafts[company.id];
        const stored = company.liveMeetingConsentNotice || {};
        return {
            // Opt-in: unconfigured companies are OFF until deliberately enabled.
            enabled: stored.enabled === true,
            message: stored.message || '',
            privacyPolicyUrl: stored.privacyPolicyUrl || '',
            repeatForLateJoiners: stored.repeatForLateJoiners !== false,
        };
    };

    setConsentDraft = (company, patch) => {
        this.setState((prev) => ({
            consentDrafts: {
                ...prev.consentDrafts,
                [company.id]: { ...this.consentValue(company), ...patch },
            },
            consentError: prev.consentError?.companyId === company.id ? null : prev.consentError,
        }));
    };

    consentDirty = (company) => company.id in this.state.consentDrafts;

    saveConsentNotice = async (company, override = {}) => {
        const draft = { ...this.consentValue(company), ...override };
        const message = draft.message.trim();
        const privacyPolicyUrl = draft.privacyPolicyUrl.trim();
        if (message.length > CONSENT_MESSAGE_MAX_LENGTH) {
            this.setState({ consentError: { companyId: company.id, message: `Message is over ${CONSENT_MESSAGE_MAX_LENGTH} characters (Google Meet truncates beyond that).` } });
            return;
        }
        if (privacyPolicyUrl && !/^https:\/\/\S+$/.test(privacyPolicyUrl)) {
            this.setState({ consentError: { companyId: company.id, message: 'Privacy policy URL must start with https://' } });
            return;
        }
        // Omit blank fields so the backend/default wording applies.
        const notice = {
            enabled: draft.enabled,
            repeatForLateJoiners: draft.repeatForLateJoiners,
            ...(message ? { message } : {}),
            ...(privacyPolicyUrl ? { privacyPolicyUrl } : {}),
        };
        this.setState({ consentSavingId: company.id, consentError: null });
        try {
            await company.updateLiveMeetingConsentNotice(notice);
            await this.syncCurrentCompany(company.id);
            this.setState((prev) => {
                const consentDrafts = { ...prev.consentDrafts };
                delete consentDrafts[company.id];
                return { consentDrafts };
            });
        } catch (error) {
            console.error('Failed to save consent notice:', error);
            const serverMessage = error?.response?.data?.error || error?.message || 'Save failed.';
            this.setState({ consentError: { companyId: company.id, message: serverMessage } });
        } finally {
            this.setState({ consentSavingId: null });
        }
    };

    // Compact cell: just the toggle + a link into the wording-editor modal.
    // The inline editor made this column ~220px wide and pushed the whole
    // grid off the page.
    renderConsentCell(company) {
        const draft = this.consentValue(company);
        const hasCustomWording = !!(company.liveMeetingConsentNotice?.message);
        return (
            <div style={{ whiteSpace: 'nowrap' }}>
                <Checkbox
                    toggle
                    checked={draft.enabled}
                    onChange={async () => {
                        // Toggle saves immediately (same UX as the other flag
                        // toggles in this grid); wording edits go via the modal.
                        this.setConsentDraft(company, { enabled: !draft.enabled });
                        await this.saveConsentNotice(company, { enabled: !draft.enabled });
                    }}
                />
                <div style={{ marginTop: '4px' }}>
                    <a
                        role="button"
                        style={{ cursor: 'pointer', fontSize: '12px' }}
                        onClick={() => this.setState({ consentModalCompanyId: company.id })}
                    >
                        {hasCustomWording ? 'Edit wording*' : 'Edit wording'}
                    </a>
                </div>
            </div>
        );
    }

    renderConsentModal(adminCompanies) {
        const companyId = this.state.consentModalCompanyId;
        if (!companyId) return null;
        const company = adminCompanies.map(ac => ac.company).find(c => c.id === companyId);
        if (!company) return null;
        const draft = this.consentValue(company);
        const error = this.state.consentError?.companyId === company.id ? this.state.consentError.message : null;
        const close = () => this.setState({ consentModalCompanyId: null });
        return (
            <Modal open size="small" onClose={close}>
                <Modal.Header>Meeting consent notice — {company.name}</Modal.Header>
                <Modal.Content>
                    <Checkbox
                        toggle
                        label="Post notice in meeting chat"
                        checked={draft.enabled}
                        onChange={() => this.setConsentDraft(company, { enabled: !draft.enabled })}
                        style={{ marginBottom: '12px' }}
                    />
                    <TextArea
                        placeholder="Custom wording (blank = Multiply default). {{companyName}} and {{privacyPolicyUrl}} are substituted."
                        value={draft.message}
                        rows={4}
                        onChange={(e) => this.setConsentDraft(company, { message: e.target.value })}
                        style={{ width: '100%', marginBottom: '2px' }}
                    />
                    <div style={{
                        fontSize: '11px',
                        textAlign: 'right',
                        color: draft.message.length > CONSENT_MESSAGE_MAX_LENGTH ? '#dc3545' : '#888',
                        marginBottom: '10px',
                    }}>
                        {draft.message.length}/{CONSENT_MESSAGE_MAX_LENGTH}
                    </div>
                    <Input
                        fluid
                        placeholder="Privacy policy URL (https://…) — appended to the message, or placed via {{privacyPolicyUrl}}"
                        value={draft.privacyPolicyUrl}
                        onChange={(e) => this.setConsentDraft(company, { privacyPolicyUrl: e.target.value })}
                        style={{ marginBottom: '10px' }}
                    />
                    <Checkbox
                        label="Repeat the notice for late joiners (Google Meet)"
                        checked={draft.repeatForLateJoiners}
                        onChange={() => this.setConsentDraft(company, { repeatForLateJoiners: !draft.repeatForLateJoiners })}
                    />
                    {error && (
                        <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '10px' }}>{error}</div>
                    )}
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={close}>Cancel</Button>
                    <Button
                        primary
                        disabled={!this.consentDirty(company)}
                        loading={this.state.consentSavingId === company.id}
                        onClick={async () => {
                            await this.saveConsentNotice(company);
                            if (!this.state.consentError) close();
                        }}
                    >
                        Save
                    </Button>
                </Modal.Actions>
            </Modal>
        );
    }

    // ---- Compliance BCC (firm email archiving) ----------------------------
    // { enabled, addresses: [email], includeExternalShareEmails }. Draft holds
    // addresses as one-per-line text; validated client-side (server re-validates).
    bccValue = (company) => {
        if (company.id in this.state.bccDrafts) return this.state.bccDrafts[company.id];
        const stored = company.complianceBcc || {};
        return {
            enabled: stored.enabled === true,
            addressesText: (stored.addresses || []).join('\n'),
            includeExternalShareEmails: stored.includeExternalShareEmails !== false,
        };
    };

    setBccDraft = (company, patch) => {
        this.setState((prev) => ({
            bccDrafts: { ...prev.bccDrafts, [company.id]: { ...this.bccValue(company), ...patch } },
            bccError: prev.bccError?.companyId === company.id ? null : prev.bccError,
        }));
    };

    saveBcc = async (company) => {
        const draft = this.bccValue(company);
        const addresses = draft.addressesText
            .split('\n').map((a) => a.trim().toLowerCase()).filter(Boolean);
        if (addresses.length > 5) {
            this.setState({ bccError: { companyId: company.id, message: 'At most 5 addresses.' } });
            return;
        }
        const bad = addresses.find((a) => !/^[^\s@;,<>"]+@[^\s@;,<>"]+\.[^\s@;,<>"]+$/.test(a));
        if (bad) {
            this.setState({ bccError: { companyId: company.id, message: `"${bad}" is not a valid email address.` } });
            return;
        }
        if (draft.enabled && addresses.length === 0) {
            this.setState({ bccError: { companyId: company.id, message: 'Enabled BCC needs at least one address.' } });
            return;
        }
        const config = {
            enabled: draft.enabled,
            includeExternalShareEmails: draft.includeExternalShareEmails,
            ...(addresses.length ? { addresses } : {}),
        };
        this.setState({ bccSavingId: company.id, bccError: null });
        try {
            await company.updateComplianceBcc(config);
            await this.syncCurrentCompany(company.id);
            this.setState((prev) => {
                const bccDrafts = { ...prev.bccDrafts };
                delete bccDrafts[company.id];
                return { bccDrafts };
            });
        } catch (error) {
            console.error('Failed to save compliance BCC:', error);
            const serverMessage = error?.response?.data?.error || error?.message || 'Save failed.';
            this.setState({ bccError: { companyId: company.id, message: serverMessage } });
        } finally {
            this.setState({ bccSavingId: null });
        }
    };

    renderBccCell(company) {
        const configured = company.complianceBcc?.enabled === true;
        return (
            <div style={{ whiteSpace: 'nowrap' }}>
                <span style={{ fontSize: '12px', color: configured ? '#0a6e35' : '#888' }}>
                    {configured ? `On (${(company.complianceBcc?.addresses || []).length})` : 'Off'}
                </span>
                <div style={{ marginTop: '4px' }}>
                    <a
                        role="button"
                        style={{ cursor: 'pointer', fontSize: '12px' }}
                        onClick={() => this.setState({ bccModalCompanyId: company.id })}
                    >
                        Edit BCC
                    </a>
                </div>
            </div>
        );
    }

    renderBccModal(adminCompanies) {
        const companyId = this.state.bccModalCompanyId;
        if (!companyId) return null;
        const company = adminCompanies.map(ac => ac.company).find(c => c.id === companyId);
        if (!company) return null;
        const draft = this.bccValue(company);
        const error = this.state.bccError?.companyId === company.id ? this.state.bccError.message : null;
        const close = () => this.setState({ bccModalCompanyId: null });
        return (
            <Modal open size="small" onClose={close}>
                <Modal.Header>Compliance BCC — {company.name}</Modal.Header>
                <Modal.Content>
                    <p style={{ fontSize: '13px', color: '#666' }}>
                        Every email the platform sends to this company's users (and, optionally, to
                        prospects on their behalf) is BCC'd to these archive addresses.
                        Takes effect in <strong>production only</strong>.
                    </p>
                    <Checkbox
                        toggle
                        label="BCC enabled"
                        checked={draft.enabled}
                        onChange={() => this.setBccDraft(company, { enabled: !draft.enabled })}
                        style={{ marginBottom: '12px' }}
                    />
                    <TextArea
                        placeholder={'Archive addresses, one per line (max 5)\ne.g. multiplyemails@rj.com'}
                        value={draft.addressesText}
                        rows={4}
                        onChange={(e) => this.setBccDraft(company, { addressesText: e.target.value })}
                        style={{ width: '100%', marginBottom: '10px' }}
                    />
                    <Checkbox
                        label="Also BCC emails sent to external prospects (share links)"
                        checked={draft.includeExternalShareEmails}
                        onChange={() => this.setBccDraft(company, { includeExternalShareEmails: !draft.includeExternalShareEmails })}
                    />
                    {error && (
                        <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '10px' }}>{error}</div>
                    )}
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={close}>Cancel</Button>
                    <Button
                        primary
                        loading={this.state.bccSavingId === company.id}
                        onClick={async () => {
                            await this.saveBcc(company);
                            if (!this.state.bccError) close();
                        }}
                    >
                        Save
                    </Button>
                </Modal.Actions>
            </Modal>
        );
    }

    renderTableRows(adminCompanies) {
        return adminCompanies.map((adminCompany, index) => <Table.Row key={`company-${index}`}>
            <Table.Cell>
                {adminCompany.company.name}
            </Table.Cell>
            <Table.Cell>
                {adminCompany.companyAdmin?.email}
            </Table.Cell>
            <Table.Cell>
                {adminCompany.totalUsersCount}
            </Table.Cell>
            <Table.Cell>
                {adminCompany.activeUsersCount}
            </Table.Cell>
            <Table.Cell>
                <Checkbox
                    toggle
                    checked={adminCompany.company.investorPitchEnabled}
                    onChange={async () => {
                        await adminCompany.company.updateInvestorPitchEnabled(
                            !adminCompany.company.investorPitchEnabled
                        );
                        await this.syncCurrentCompany(adminCompany.company.id);
                    }}
                />
            </Table.Cell>
            <Table.Cell>
                <Checkbox
                    toggle
                    checked={adminCompany.company.liveMeetingsEnabled}
                    onChange={async () => {
                        await adminCompany.company.updateLiveMeetingsEnabled(
                            !adminCompany.company.liveMeetingsEnabled
                        );
                        await this.syncCurrentCompany(adminCompany.company.id);
                    }}
                />
            </Table.Cell>
            <Table.Cell>
                {this.renderLoomCell(adminCompany.company)}
            </Table.Cell>
            <Table.Cell>
                {this.renderConsentCell(adminCompany.company)}
            </Table.Cell>
            <Table.Cell>
                {this.renderBccCell(adminCompany.company)}
            </Table.Cell>
        </Table.Row>);
    }

    render() {
        const { adminCompanies } = this.props;

        return (
            // The grid outgrew the page width (8 columns incl. two editor
            // cells) — scroll the table inside its own container rather than
            // clipping the right-hand columns. The sticky strip below mirrors
            // this container's horizontal scrollbar at the viewport bottom so
            // admins don't have to scroll to the end of the table to find it.
            <div>
            <div
                style={{
                    overflow: 'auto',
                    // Fit the viewport so the container owns vertical scrolling
                    // too: the sticky header row pins to its top and the
                    // horizontal scrollbar stays visible at its bottom.
                    maxHeight: 'calc(100vh - 230px)',
                }}
                ref={this.tableScrollRef}
                onScroll={() => this.syncScroll(this.tableScrollRef, this.stickyScrollRef)}
            >
            <Table striped>
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell style={HEADER_STICKY}>
                            Company Name
                        </Table.HeaderCell>
                        <Table.HeaderCell style={HEADER_STICKY}>
                            Company Admin Email
                        </Table.HeaderCell>
                        <Table.HeaderCell style={HEADER_STICKY}>
                            Total Users
                        </Table.HeaderCell>
                        <Table.HeaderCell style={HEADER_STICKY}>
                            Active Users
                        </Table.HeaderCell>
                        <Table.HeaderCell style={HEADER_STICKY}>
                            Investor Pitch
                        </Table.HeaderCell>
                        <Table.HeaderCell style={HEADER_STICKY}>
                            Live Meetings
                        </Table.HeaderCell>
                        <Table.HeaderCell style={HEADER_STICKY}>
                            Training Plan Looms
                        </Table.HeaderCell>
                        <Table.HeaderCell style={HEADER_STICKY}>
                            Consent Notice
                        </Table.HeaderCell>
                        <Table.HeaderCell style={HEADER_STICKY}>
                            Compliance BCC
                        </Table.HeaderCell>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {this.renderTableRows(adminCompanies)}
                </Table.Body>
            </Table>
            </div>
            {this.state.stickyVisible && (
                <div
                    ref={this.stickyScrollRef}
                    onScroll={() => this.syncScroll(this.stickyScrollRef, this.tableScrollRef)}
                    style={{
                        position: 'fixed',
                        bottom: 0,
                        left: this.state.stickyLeft,
                        width: this.state.stickyWidth,
                        overflowX: 'auto',
                        overflowY: 'hidden',
                        zIndex: 50,
                        background: 'rgba(255, 255, 255, 0.9)',
                        borderTop: '1px solid #e0e0e0',
                    }}
                >
                    {/* Spacer as wide as the table so this strip scrolls in lockstep. */}
                    <div style={{ width: this.state.tableScrollWidth, height: 1 }} />
                </div>
            )}
            {this.renderConsentModal(adminCompanies)}
            {this.renderBccModal(adminCompanies)}
            </div>
        );
    }
}

export default observer(CompaniesGrid);
